import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, FileText, ChevronDown, ChevronUp, Plus, Printer } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { posApi, saasApi } from '@/services/api';
import { isLocalFirstMode, listLocalCustomerInvoices } from '@/offline';
import { useSyncStore } from '@/store/syncStore';
import { useShiftStore } from '@/store/shiftStore';
import { useAuthStore } from '@/store/authStore';
import { isCompanyAdmin } from '@/types';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import BankAccountSelect from '@/components/common/BankAccountSelect';
import Receipt from '@/components/pos/Receipt';
import { translateInvoiceStatus, translatePaymentMethod } from '@/utils/translateStatus';
import { formatMoney, getCurrencySymbol } from '@/utils/currency';

interface SaleInvoiceLedgerModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: string;
  customerName?: string;
}

export default function SaleInvoiceLedgerModal({
  isOpen,
  onClose,
  customer,
  customerName,
}: SaleInvoiceLedgerModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { role } = useAuthStore();
  const openShift = useShiftStore((s) => s.openShift);
  const [expandedInvoice, setExpandedInvoice] = useState<string | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState<any>(null);
  const [receiptInvoiceName, setReceiptInvoiceName] = useState<string | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [bankAccount, setBankAccount] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [attachment, setAttachment] = useState<File | null>(null);

  const {
    dateRange, setDateRange,
    customFrom, setCustomFrom,
    customTo, setCustomTo,
    from_date, to_date,
  } = useReportDateRange('This Month');
  const [statusFilter, setStatusFilter] = useState('Unpaid');

  const posSyncMode = useSyncStore((s) => s.posSyncMode);
  const catalogSyncedAt = useSyncStore((s) => s.catalogSyncedAt);

  const { data: invoices, isLoading } = useQuery({
    queryKey: ['customerInvoices', customer, from_date, to_date, statusFilter, posSyncMode, catalogSyncedAt],
    queryFn: async () => {
      if (isLocalFirstMode()) {
        return listLocalCustomerInvoices({
          customer,
          from_date: from_date || undefined,
          to_date: to_date || undefined,
          status: statusFilter || undefined,
        });
      }
      const res = await posApi.getCustomerInvoices({
        customer,
        ...(from_date ? { from_date } : {}),
        ...(to_date ? { to_date } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
      });
      return res.data.message || [];
    },
    enabled: isOpen && !!customer,
  });

  const { data: banks = [] } = useQuery({
    queryKey: ['bankAccounts'],
    queryFn: async () => {
      const res = await posApi.getBankAccounts();
      return res.data.message || [];
    },
    enabled: isOpen,
  });

  const addPaymentMutation = useMutation({
    mutationFn: async () => {
      if (!selectedInvoiceForPayment) return;
      if (Number(paymentAmount) > 0 && paymentMethod === 'Bank' && !bankAccount) {
        throw new Error('Select a bank');
      }
      const bank = banks.find((b: { name: string; bank_name: string }) => b.name === bankAccount);
      let fileUrl = '';
      if (attachment) {
        const formData = new FormData();
        formData.append('file', attachment);
        const uploadRes = await saasApi.uploadFile(formData);
        fileUrl = uploadRes.data?.message?.file_url || '';
      }
      await posApi.addSalePayment(selectedInvoiceForPayment.name, {
        amount: Number(paymentAmount),
        attachment: fileUrl,
        payment_method: paymentMethod,
        bank_account: paymentMethod === 'Bank' ? bankAccount : '',
        bank_name: paymentMethod === 'Bank' ? (bank?.bank_name || '') : '',
        reference_no: paymentRef,
        date: paymentDate,
        account: paymentMethod === 'Cash'
          ? (openShift?.pocket_name || '')
          : (bank?.bank_name || ''),
        ...(openShift && !isCompanyAdmin(role) ? { pos_shift: openShift.name } : {}),
      });
    },
    onSuccess: () => {
      toast.success(t('saleInvoices.paymentAdded'));
      queryClient.invalidateQueries({ queryKey: ['customerInvoices', customer] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setPaymentModalOpen(false);
      setPaymentAmount('');
      setPaymentRef('');
      setPaymentMethod('Cash');
      setBankAccount('');
      setAttachment(null);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || err.message || t('saleInvoices.paymentFailed'));
    },
  });

  if (!isOpen) return null;

  const statusClass = (status: string) => {
    if (status === 'Paid') return 'bg-emerald-100 text-emerald-700';
    if (status === 'Partially Paid') return 'bg-yellow-100 text-yellow-700';
    if (status === 'Unpaid') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-primary-50 text-primary-600 rounded-lg flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 text-base sm:text-lg">{t('saleInvoices.title')}</h3>
              <p className="text-sm text-gray-500 truncate">{customerName || customer}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors shrink-0">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="px-4 pt-4 pb-2 border-b border-gray-100 bg-gray-50/50">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <ReportDateRangeFilter
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              customFrom={customFrom}
              customTo={customTo}
              onCustomFromChange={setCustomFrom}
              onCustomToChange={setCustomTo}
              className="flex-1"
            />
            <div className="w-full sm:w-48">
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('saleInvoices.status')}</label>
              <select
                className="input-field py-1.5 text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="Unpaid">{translateInvoiceStatus('Unpaid', t)}</option>
                <option value="Paid">{translateInvoiceStatus('Paid', t)}</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : invoices?.length === 0 ? (
            <div className="text-center py-12 text-gray-500">{t('saleInvoices.empty')}</div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              {/* Header row (desktop) */}
              <div className="hidden sm:grid grid-cols-[minmax(0,1.4fr)_0.9fr_0.7fr_0.9fr_0.9fr_auto] gap-3 px-3 py-2 bg-gray-50 border-b border-gray-100 text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                <div>{t('saleInvoices.invoice')}</div>
                <div>{t('common.date')}</div>
                <div>{t('common.status')}</div>
                <div className="text-right">{t('saleInvoices.netTotal')}</div>
                <div className="text-right">{t('saleInvoices.balance')}</div>
                <div className="min-w-[100px]" />
              </div>

              <div className="divide-y divide-gray-100">
                {invoices?.map((inv: any) => {
                  const balanceDue = Math.max(0, Number(inv.balance_amount) || 0);
                  const isPaid = inv.status === 'Paid' || balanceDue <= 0;
                  const isExpanded = expandedInvoice === inv.name;

                  return (
                    <div
                      key={inv.name}
                      className={isExpanded ? 'bg-gray-50/40' : 'bg-white'}
                    >
                      <div
                        className="px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => setExpandedInvoice(isExpanded ? null : inv.name)}
                      >
                        {/* Desktop */}
                        <div className="hidden sm:grid grid-cols-[minmax(0,1.4fr)_0.9fr_0.7fr_0.9fr_0.9fr_auto] gap-3 items-center">
                          <div className="min-w-0 flex items-center gap-2">
                            <span className="font-medium text-gray-900 text-sm truncate">{inv.name}</span>
                          </div>

                          <div className="text-sm text-gray-600">
                            {inv.invoice_date ? format(new Date(inv.invoice_date), 'dd MMM yyyy') : '-'}
                          </div>

                          <div>
                            <span className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${statusClass(inv.status)}`}>
                              {translateInvoiceStatus(inv.status, t)}
                            </span>
                          </div>

                          <div className="text-sm font-medium text-gray-900 text-right">
                            {formatMoney(Number(inv.net_total || 0))}
                          </div>

                          <div
                            className={`text-sm font-medium text-right ${balanceDue > 0 ? 'text-amber-600' : 'text-emerald-600'}`}
                          >
                            {formatMoney(balanceDue)}
                          </div>

                          <div className="flex items-center justify-end gap-1.5 shrink-0 flex-nowrap min-w-[100px]">
                            {!isPaid && !isLocalFirstMode() && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedInvoiceForPayment(inv);
                                  setPaymentAmount(String(balanceDue));
                                  setPaymentModalOpen(true);
                                }}
                                className="shrink-0 px-2 py-1 text-[11px] bg-primary-50 text-primary-600 hover:bg-primary-100 font-medium rounded transition-colors inline-flex items-center gap-0.5 whitespace-nowrap"
                              >
                                <Plus className="w-3 h-3" /> {t('common.pay')}
                              </button>
                            )}
                            <button
                              type="button"
                              title={t('saleInvoices.reprint')}
                              onClick={(e) => {
                                e.stopPropagation();
                                setReceiptInvoiceName(inv.name);
                              }}
                              className="inline-flex items-center justify-center p-1.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                            >
                              <Printer className="w-4 h-4" />
                            </button>
                            <div className="text-gray-400">
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Mobile */}
                        <div className="sm:hidden">
                          <div className="grid grid-cols-[1fr_auto] items-start gap-2">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="font-medium text-gray-900 text-sm truncate">{inv.name}</span>
                                <span className={`shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded ${statusClass(inv.status)}`}>
                                  {translateInvoiceStatus(inv.status, t)}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0 flex-nowrap">
                              {!isPaid && !isLocalFirstMode() && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedInvoiceForPayment(inv);
                                    setPaymentAmount(String(balanceDue));
                                    setPaymentModalOpen(true);
                                  }}
                                  className="shrink-0 px-2 py-1 text-[11px] bg-primary-50 text-primary-600 hover:bg-primary-100 font-medium rounded transition-colors inline-flex items-center gap-0.5 whitespace-nowrap"
                                >
                                  <Plus className="w-3 h-3" /> {t('common.pay')}
                                </button>
                              )}
                              <button
                                type="button"
                                title={t('saleInvoices.reprint')}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setReceiptInvoiceName(inv.name);
                                }}
                                className="inline-flex items-center justify-center p-1.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                              >
                                <Printer className="w-4 h-4" />
                              </button>
                              <div className="text-gray-400">
                                {isExpanded ? (
                                  <ChevronUp className="w-4 h-4" />
                                ) : (
                                  <ChevronDown className="w-4 h-4" />
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="mt-1 grid grid-cols-3 gap-2 text-xs">
                            <div>
                              <div className="text-gray-400">{t('common.date')}</div>
                              <div className="text-gray-700">
                                {inv.invoice_date ? format(new Date(inv.invoice_date), 'dd MMM yyyy') : '-'}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-gray-400">Net</div>
                              <div className="font-medium text-gray-900">
                                {formatMoney(Number(inv.net_total || 0))}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-gray-400">Bal</div>
                              <div
                                className={`font-medium ${balanceDue > 0 ? 'text-amber-600' : 'text-emerald-600'}`}
                              >
                                {formatMoney(balanceDue)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expanded payments */}
                      {isExpanded && (
                        <div className="border-t border-gray-100 p-3 sm:p-4 bg-gray-50/50 space-y-4">
                          {Number(inv.write_off_amount) > 0 && (
                            <p className="text-sm text-gray-600">
                              Write off: {formatMoney(Number(inv.write_off_amount))}
                            </p>
                          )}
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Payments</h4>
                            {inv.payments?.length > 0 ? (
                              <>
                                <div className="hidden sm:block">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="text-left text-xs text-gray-500 uppercase">
                                        <th className="pb-2">Date</th>
                                        <th className="pb-2">Method</th>
                                        <th className="pb-2">Reference</th>
                                        <th className="pb-2 text-right">Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {inv.payments.map((payment: any, idx: number) => (
                                        <tr key={idx} className="border-t border-gray-100">
                                          <td className="py-2 text-gray-600">
                                            {payment.date ? format(new Date(payment.date), 'dd MMM yyyy') : '-'}
                                          </td>
                                          <td className="py-2 text-gray-600">
                                            {payment.payment_method}
                                            {payment.bank_name ? ` (${payment.bank_name})` : payment.account && payment.payment_method === 'Bank' ? ` (${payment.account})` : ''}
                                          </td>
                                          <td className="py-2 text-gray-600">{payment.reference_no || '-'}</td>
                                          <td className="py-2 text-right font-medium text-emerald-600">
                                            {formatMoney(Number(payment.amount))}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                <div className="sm:hidden space-y-2">
                                  {inv.payments.map((payment: any, idx: number) => (
                                    <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3">
                                      <div className="flex justify-between items-start gap-2">
                                        <div>
                                          <div className="text-sm font-medium text-gray-900">
                                            {payment.payment_method}
                                            {payment.bank_name ? ` (${payment.bank_name})` : payment.account && payment.payment_method === 'Bank' ? ` (${payment.account})` : ''}
                                          </div>
                                          <div className="text-xs text-gray-500">
                                            {payment.date ? format(new Date(payment.date), 'dd MMM yyyy') : '-'}
                                            {payment.reference_no ? ` · ${payment.reference_no}` : ''}
                                          </div>
                                        </div>
                                        <div className="text-sm font-medium text-emerald-600 whitespace-nowrap">
                                          {formatMoney(Number(payment.amount))}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </>
                            ) : (
                              <p className="text-sm text-gray-400">No payments recorded yet.</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {receiptInvoiceName && (
        <Receipt
          invoiceName={receiptInvoiceName}
          onClose={() => setReceiptInvoiceName(null)}
        />
      )}

      {paymentModalOpen && selectedInvoiceForPayment && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4">
          <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-sm max-h-[95vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 shrink-0">
              <h4 className="font-semibold text-gray-900">{t('saleInvoices.addPayment')}</h4>
              <button type="button" onClick={() => { setPaymentModalOpen(false); setAttachment(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.date')}</label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.paymentMethod')}</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => {
                    const next = e.target.value;
                    setPaymentMethod(next);
                    if (next === 'Bank' && !bankAccount && banks.length) {
                      const def = banks.find((b: { is_default: number }) => b.is_default) || banks[0];
                      setBankAccount(def.name);
                    }
                    if (next !== 'Bank') setBankAccount('');
                  }}
                  className="input-field w-full"
                >
                  <option value="Cash">{translatePaymentMethod('Cash', t)}</option>
                  <option value="Bank">{translatePaymentMethod('Bank', t)}</option>
                </select>
              </div>
              {paymentMethod === 'Bank' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank *</label>
                  <BankAccountSelect
                    value={bankAccount}
                    onChange={setBankAccount}
                    banks={banks}
                    required
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.amount')} ({getCurrencySymbol()})</label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  max={selectedInvoiceForPayment.balance_amount}
                  className="input-field w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Balance due: {formatMoney(Number(selectedInvoiceForPayment.balance_amount))}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('supplierPayment.referenceOptional')}</label>
                <input
                  type="text"
                  value={paymentRef}
                  onChange={(e) => setPaymentRef(e.target.value)}
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Attachment</label>
                <input
                  type="file"
                  onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                />
              </div>
            </div>
            <div className="p-4 sm:p-5 border-t border-gray-100 bg-gray-50 flex gap-3 shrink-0">
              <button
                type="button"
                onClick={() => { setPaymentModalOpen(false); setAttachment(null); }}
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                disabled={addPaymentMutation.isPending || !paymentAmount || Number(paymentAmount) <= 0}
                onClick={() => addPaymentMutation.mutate()}
                className="w-full sm:flex-1 btn-primary py-2.5 disabled:opacity-50"
              >
                {addPaymentMutation.isPending ? t('common.saving') : t('supplierPayment.savePayment')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
